import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { getSignedStorageUrl } from '@/utils/storageUtils';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from '@/hooks/use-toast';
import { mockInspectionTemplates, mockInspections, mockInspectionTemplateSections, mockInspectionTemplateItems } from '@/data/mockData';
import type { 
  InspectionResponseType,
  InspectionStatus,
  InspectionTemplate, 
  InspectionTemplateSection, 
  InspectionTemplateItem,
  Inspection,
  InspectionResponse,
  TemplateWithSections,
  CSVImportRow
} from '@/types/inspection';

// Templates
export function useInspectionTemplates() {
  const { isDemoMode } = useAuth();
  
  return useQuery({
    queryKey: ['inspection-templates', isDemoMode],
    queryFn: async () => {
      if (isDemoMode) {
        return mockInspectionTemplates as InspectionTemplate[];
      }
      const { data, error } = await supabase
        .from('inspection_templates')
        .select('*')
        .order('name');
      
      if (error) throw error;
      return data as InspectionTemplate[];
    }
  });
}

export function useInspectionTemplate(templateId: string | null) {
  const { isDemoMode } = useAuth();
  
  return useQuery({
    queryKey: ['inspection-template', templateId, isDemoMode],
    queryFn: async () => {
      if (!templateId) return null;

      if (isDemoMode) {
        const template = mockInspectionTemplates.find(t => t.id === templateId);
        if (!template) return null;
        const sections = mockInspectionTemplateSections
          .filter(s => s.template_id === templateId)
          .map(section => ({
            ...section,
            items: mockInspectionTemplateItems.filter(i => i.section_id === section.id)
          }));
        return { ...template, sections } as TemplateWithSections;
      }

      // Get template
      const { data: template, error: templateError } = await supabase
        .from('inspection_templates')
        .select('*')
        .eq('id', templateId)
        .single();
      
      if (templateError) throw templateError;

      // Get sections
      const { data: sections, error: sectionsError } = await supabase
        .from('inspection_template_sections')
        .select('*')
        .eq('template_id', templateId)
        .order('sort_order');
      
      if (sectionsError) throw sectionsError;

      // Get items for all sections
      const sectionIds = sections.map(s => s.id);
      const { data: items, error: itemsError } = await supabase
        .from('inspection_template_items')
        .select('*')
        .in('section_id', sectionIds)
        .order('sort_order');
      
      if (itemsError) throw itemsError;

      // Combine sections with their items
      const sectionsWithItems = sections.map(section => ({
        ...section,
        items: items.filter(item => item.section_id === section.id)
      }));

      return {
        ...template,
        sections: sectionsWithItems
      } as TemplateWithSections;
    },
    enabled: !!templateId
  });
}

export function useCreateTemplate() {
  const queryClient = useQueryClient();
  const { user } = useAuth();

  return useMutation({
    mutationFn: async (data: { name: string; description?: string }) => {
      const { data: template, error } = await supabase
        .from('inspection_templates')
        .insert({
          name: data.name,
          description: data.description || null,
          created_by: user?.id
        })
        .select()
        .single();
      
      if (error) throw error;
      return template;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['inspection-templates'] });
      toast({ title: 'Template created successfully' });
    },
    onError: (error: Error) => {
      toast({ title: 'Error creating template', description: error.message, variant: 'destructive' });
    }
  });
}

export function useUpdateTemplate() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: { id: string; name: string; description?: string; is_active?: boolean }) => {
      const { error } = await supabase
        .from('inspection_templates')
        .update({
          name: data.name,
          description: data.description,
          is_active: data.is_active
        })
        .eq('id', data.id);
      
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['inspection-templates'] });
      toast({ title: 'Template updated successfully' });
    },
    onError: (error: Error) => {
      toast({ title: 'Error updating template', description: error.message, variant: 'destructive' });
    }
  });
}

export function useDeleteTemplate() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (templateId: string) => {
      const { error } = await supabase
        .from('inspection_templates')
        .delete()
        .eq('id', templateId);
      
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['inspection-templates'] });
      toast({ title: 'Template deleted successfully' });
    },
    onError: (error: Error) => {
      toast({ title: 'Error deleting template', description: error.message, variant: 'destructive' });
    }
  });
}

// Sections - Query by template ID
export function useInspectionTemplateSections(templateId: string | null) {
  const queryClient = useQueryClient();

  const query = useQuery({
    queryKey: ['inspection-template-sections', templateId],
    queryFn: async () => {
      if (!templateId) return [];
      const { data, error } = await supabase
        .from('inspection_template_sections')
        .select('*')
        .eq('template_id', templateId)
        .order('sort_order');
      if (error) throw error;
      return data as InspectionTemplateSection[];
    },
    enabled: !!templateId
  });

  const createSectionMutation = useMutation({
    mutationFn: async (data: { template_id: string; name: string; subsection?: string | null; sort_order: number }) => {
      const { data: section, error } = await supabase
        .from('inspection_template_sections')
        .insert(data)
        .select()
        .single();
      if (error) throw error;
      return section;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['inspection-template-sections', templateId] });
      queryClient.invalidateQueries({ queryKey: ['inspection-template', templateId] });
    }
  });

  const updateSectionMutation = useMutation({
    mutationFn: async (data: { id: string; name?: string; subsection?: string | null; sort_order?: number }) => {
      const { id, ...updateData } = data;
      const { error } = await supabase
        .from('inspection_template_sections')
        .update(updateData)
        .eq('id', id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['inspection-template-sections', templateId] });
    }
  });

  const deleteSectionMutation = useMutation({
    mutationFn: async (sectionId: string) => {
      const { error } = await supabase
        .from('inspection_template_sections')
        .delete()
        .eq('id', sectionId);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['inspection-template-sections', templateId] });
      queryClient.invalidateQueries({ queryKey: ['inspection-template', templateId] });
    }
  });

  return {
    sections: query.data,
    isLoading: query.isLoading,
    createSection: createSectionMutation.mutateAsync,
    updateSection: updateSectionMutation.mutateAsync,
    deleteSection: deleteSectionMutation.mutateAsync
  };
}

// Items - Query by template ID (fetches all items for all sections in a template)
export function useInspectionTemplateItems(templateId: string | null) {
  const queryClient = useQueryClient();

  const query = useQuery({
    queryKey: ['inspection-template-items', templateId],
    queryFn: async () => {
      if (!templateId) return [];
      // First get sections for this template
      const { data: sections, error: sectionsError } = await supabase
        .from('inspection_template_sections')
        .select('id')
        .eq('template_id', templateId);
      if (sectionsError) throw sectionsError;
      if (!sections.length) return [];

      const sectionIds = sections.map(s => s.id);
      const { data, error } = await supabase
        .from('inspection_template_items')
        .select('*')
        .in('section_id', sectionIds)
        .order('sort_order');
      if (error) throw error;
      return data as InspectionTemplateItem[];
    },
    enabled: !!templateId
  });

  const createItemMutation = useMutation({
    mutationFn: async (data: {
      section_id: string;
      item_code?: string;
      item_text: string;
      response_type: string;
      notes_required_if_fail?: boolean;
      photo_required_if_fail?: boolean;
      always_require_notes?: boolean;
      dropdown_options?: string[];
      sort_order: number;
    }) => {
      const { data: item, error } = await supabase
        .from('inspection_template_items')
        .insert({
          ...data,
          response_type: data.response_type as InspectionResponseType
        })
        .select()
        .single();
      if (error) throw error;
      return item;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['inspection-template-items', templateId] });
      queryClient.invalidateQueries({ queryKey: ['inspection-template', templateId] });
    }
  });

  const updateItemMutation = useMutation({
    mutationFn: async (data: Partial<InspectionTemplateItem> & { id: string }) => {
      const { id, ...updateData } = data;
      const { error } = await supabase
        .from('inspection_template_items')
        .update(updateData)
        .eq('id', id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['inspection-template-items', templateId] });
    }
  });

  const deleteItemMutation = useMutation({
    mutationFn: async (itemId: string) => {
      const { error } = await supabase
        .from('inspection_template_items')
        .delete()
        .eq('id', itemId);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['inspection-template-items', templateId] });
      queryClient.invalidateQueries({ queryKey: ['inspection-template', templateId] });
    }
  });

  return {
    items: query.data,
    isLoading: query.isLoading,
    createItem: createItemMutation.mutateAsync,
    updateItem: updateItemMutation.mutateAsync,
    deleteItem: deleteItemMutation.mutateAsync
  };
}

export function useCreateSection() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: { template_id: string; name: string; subsection?: string; sort_order: number }) => {
      const { data: section, error } = await supabase
        .from('inspection_template_sections')
        .insert(data)
        .select()
        .single();
      
      if (error) throw error;
      return section;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['inspection-template', variables.template_id] });
      queryClient.invalidateQueries({ queryKey: ['inspection-template-sections', variables.template_id] });
    },
    onError: (error: Error) => {
      toast({ title: 'Error creating section', description: error.message, variant: 'destructive' });
    }
  });
}

export function useDeleteSection() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ sectionId, templateId }: { sectionId: string; templateId: string }) => {
      const { error } = await supabase
        .from('inspection_template_sections')
        .delete()
        .eq('id', sectionId);
      
      if (error) throw error;
      return templateId;
    },
    onSuccess: (templateId) => {
      queryClient.invalidateQueries({ queryKey: ['inspection-template', templateId] });
    },
    onError: (error: Error) => {
      toast({ title: 'Error deleting section', description: error.message, variant: 'destructive' });
    }
  });
}

// Items
export function useCreateItem() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: { 
      section_id: string; 
      item_code?: string;
      item_text: string; 
      response_type: string;
      notes_required_if_fail?: boolean;
      photo_required_if_fail?: boolean;
      always_require_notes?: boolean;
      dropdown_options?: string[];
      sort_order: number;
      templateId: string;
    }) => {
      const { templateId, ...insertData } = data;
      const { data: item, error } = await supabase
        .from('inspection_template_items')
        .insert({
          ...insertData,
          response_type: insertData.response_type as InspectionResponseType
        })
        .select()
        .single();
      
      if (error) throw error;
      return { item, templateId };
    },
    onSuccess: ({ templateId }) => {
      queryClient.invalidateQueries({ queryKey: ['inspection-template', templateId] });
    },
    onError: (error: Error) => {
      toast({ title: 'Error creating item', description: error.message, variant: 'destructive' });
    }
  });
}

export function useDeleteItem() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ itemId, templateId }: { itemId: string; templateId: string }) => {
      const { error } = await supabase
        .from('inspection_template_items')
        .delete()
        .eq('id', itemId);
      
      if (error) throw error;
      return templateId;
    },
    onSuccess: (templateId) => {
      queryClient.invalidateQueries({ queryKey: ['inspection-template', templateId] });
    },
    onError: (error: Error) => {
      toast({ title: 'Error deleting item', description: error.message, variant: 'destructive' });
    }
  });
}

// CSV Import
export function useImportTemplate() {
  const queryClient = useQueryClient();
  const { user } = useAuth();

  return useMutation({
    mutationFn: async (rows: CSVImportRow[]) => {
      // Group by template name
      const templateGroups = new Map<string, CSVImportRow[]>();
      rows.forEach(row => {
        if (!templateGroups.has(row.template_name)) {
          templateGroups.set(row.template_name, []);
        }
        templateGroups.get(row.template_name)!.push(row);
      });

      const results: string[] = [];

      for (const [templateName, templateRows] of templateGroups) {
        // Create template
        const { data: template, error: templateError } = await supabase
          .from('inspection_templates')
          .insert({
            name: templateName,
            created_by: user?.id
          })
          .select()
          .single();

        if (templateError) throw templateError;

        // Group by section/subsection
        const sectionMap = new Map<string, { subsection: string | null; items: CSVImportRow[] }>();
        templateRows.forEach(row => {
          const sectionKey = row.section || '_META_';
          if (!sectionMap.has(sectionKey)) {
            sectionMap.set(sectionKey, { subsection: row.subsection || null, items: [] });
          }
          sectionMap.get(sectionKey)!.items.push(row);
        });

        let sectionOrder = 0;
        for (const [sectionName, sectionData] of sectionMap) {
          // Create section
          const { data: section, error: sectionError } = await supabase
            .from('inspection_template_sections')
            .insert({
              template_id: template.id,
              name: sectionName === '_META_' ? 'Meta Information' : sectionName,
              subsection: sectionData.subsection,
              sort_order: sectionOrder++
            })
            .select()
            .single();

          if (sectionError) throw sectionError;

          // Create items
          const items = sectionData.items.map((row, idx) => ({
            section_id: section.id,
            item_code: row.item_code || null,
            item_text: row.item_text,
            response_type: mapResponseType(row.response_type_suggested) as InspectionResponseType,
            notes_required_if_fail: row.notes_required_if_fail?.toLowerCase() === 'true',
            photo_required_if_fail: row.photo_required_if_fail?.toLowerCase() === 'true',
            sort_order: parseInt(row.sort_order) || idx
          }));

          const { error: itemsError } = await supabase
            .from('inspection_template_items')
            .insert(items);

          if (itemsError) throw itemsError;
        }

        results.push(templateName);
      }

      return results;
    },
    onSuccess: (templateNames) => {
      queryClient.invalidateQueries({ queryKey: ['inspection-templates'] });
      toast({ title: `Imported ${templateNames.length} template(s)`, description: templateNames.join(', ') });
    },
    onError: (error: Error) => {
      toast({ title: 'Error importing templates', description: error.message, variant: 'destructive' });
    }
  });
}

function mapResponseType(type: string): string {
  const mapping: Record<string, string> = {
    'PASS_FAIL_NA': 'PASS_FAIL_NA',
    'YES_NO': 'YES_NO',
    'TEXT': 'TEXT',
    'NUMBER': 'NUMBER',
    'DROPDOWN': 'DROPDOWN',
    'META': 'META'
  };
  return mapping[type?.toUpperCase()] || 'PASS_FAIL_NA';
}

// Inspections
export function useInspections(filters?: { 
  status?: string; 
  site_id?: string; 
  template_id?: string;
  inspector_id?: string;
  date_from?: string;
  date_to?: string;
}) {
  const { isDemoMode } = useAuth();
  
  return useQuery({
    queryKey: ['inspections', filters, isDemoMode],
    queryFn: async () => {
      if (isDemoMode) {
        let filtered = [...mockInspections];
        if (filters?.status) {
          filtered = filtered.filter(i => i.status === filters.status);
        }
        if (filters?.site_id) {
          filtered = filtered.filter(i => i.site_id === filters.site_id);
        }
        if (filters?.template_id) {
          filtered = filtered.filter(i => i.template_id === filters.template_id);
        }
        return filtered as unknown as Inspection[];
      }

      console.log('[useInspections] Fetching inspections with filters:', filters);
      
      let query = supabase
        .from('inspections')
        .select(`
          *,
          inspection_templates(id, name),
          sites(id, name)
        `)
        .order('created_at', { ascending: false });

      if (filters?.status && (filters.status === 'draft' || filters.status === 'submitted')) {
        query = query.eq('status', filters.status);
      }
      if (filters?.site_id) {
        query = query.eq('site_id', filters.site_id);
      }
      if (filters?.template_id) {
        query = query.eq('template_id', filters.template_id);
      }
      if (filters?.inspector_id) {
        query = query.eq('inspector_id', filters.inspector_id);
      }
      if (filters?.date_from) {
        query = query.gte('started_at', filters.date_from);
      }
      if (filters?.date_to) {
        query = query.lte('started_at', filters.date_to);
      }

      const { data, error } = await query;
      
      if (error) {
        console.error('[useInspections] Query error:', error);
        throw error;
      }
      
      console.log('[useInspections] Found inspections:', data?.length || 0);
      
      // Transform to match our types
      return data.map(item => ({
        ...item,
        template: item.inspection_templates as any,
        site: item.sites as any
      })) as Inspection[];
    }
  });
}

export function useInspection(inspectionId: string | null) {
  const queryClient = useQueryClient();

  const query = useQuery({
    queryKey: ['inspection', inspectionId],
    queryFn: async () => {
      if (!inspectionId) return null;

      const { data, error } = await supabase
        .from('inspections')
        .select(`
          *,
          inspection_templates(id, name),
          sites(id, name)
        `)
        .eq('id', inspectionId)
        .maybeSingle();
      
      if (error) throw error;
      if (!data) return null;
      
      return {
        ...data,
        template: data.inspection_templates as any,
        site: data.sites as any,
      } as Inspection;
    },
    enabled: !!inspectionId
  });

  const updateInspectionMutation = useMutation({
    mutationFn: async (data: {
      id: string;
      status?: 'draft' | 'submitted';
      notes?: string;
      meta_data?: Record<string, string>;
      submitted_at?: string;
    }) => {
      const { id, ...updateData } = data;
      const { error } = await supabase
        .from('inspections')
        .update(updateData)
        .eq('id', id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['inspection', inspectionId] });
      queryClient.invalidateQueries({ queryKey: ['inspections'] });
    }
  });

  return {
    inspection: query.data,
    isLoading: query.isLoading,
    isError: query.isError,
    error: (query.error as Error) || null,
    updateInspection: updateInspectionMutation.mutateAsync,
  };
}

export function useCreateInspection() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: { 
      template_id: string; 
      site_id?: string; 
      area?: string;
    }) => {
      // Get current session directly from Supabase to avoid race conditions with auth context
      const { data: { session } } = await supabase.auth.getSession();
      const inspectorId = session?.user?.id;

      if (!inspectorId) {
        throw new Error('Please sign in with a real account (email login) to create inspections. Demo usernames cannot create database inspections.');
      }

      const { data: inspection, error } = await supabase
        .from('inspections')
        .insert({
          template_id: data.template_id,
          site_id: data.site_id || null,
          area: data.area || null,
          inspector_id: inspectorId,
          status: 'draft'
        })
        .select()
        .single();
      
      if (error) throw error;
      return inspection as Inspection;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['inspections'] });
    },
    onError: (error: Error) => {
      toast({ title: 'Error creating inspection', description: error.message, variant: 'destructive' });
    }
  });
}

export function useUpdateInspection() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: { 
      id: string; 
      status?: InspectionStatus; 
      notes?: string;
      meta_data?: Record<string, string>;
      submitted_at?: string;
    }) => {
      const { id, ...updateData } = data;
      const { error } = await supabase
        .from('inspections')
        .update(updateData)
        .eq('id', id);
      
      if (error) throw error;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['inspection', variables.id] });
      queryClient.invalidateQueries({ queryKey: ['inspections'] });
    },
    onError: (error: Error) => {
      toast({ title: 'Error updating inspection', description: error.message, variant: 'destructive' });
    }
  });
}

// Responses
export function useInspectionResponses(inspectionId: string | null) {
  const queryClient = useQueryClient();

  const query = useQuery({
    queryKey: ['inspection-responses', inspectionId],
    queryFn: async () => {
      if (!inspectionId) return [];

      const { data, error } = await supabase
        .from('inspection_responses')
        .select('*')
        .eq('inspection_id', inspectionId);
      
      if (error) throw error;
      return data as InspectionResponse[];
    },
    enabled: !!inspectionId
  });

  const createResponseMutation = useMutation({
    mutationFn: async (data: {
      inspection_id: string;
      template_item_id: string;
      response_value?: string | null;
      notes?: string;
      photo_urls?: string[];
    }) => {
      const { data: response, error } = await supabase
        .from('inspection_responses')
        .insert(data)
        .select()
        .single();
      if (error) throw error;
      return response;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['inspection-responses', inspectionId] });
    }
  });

  const updateResponseMutation = useMutation({
    mutationFn: async (data: { id: string; response_value?: string | null; notes?: string; photo_urls?: string[] }) => {
      const { id, ...updateData } = data;
      const { error } = await supabase
        .from('inspection_responses')
        .update(updateData)
        .eq('id', id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['inspection-responses', inspectionId] });
    }
  });

  return {
    responses: query.data,
    isLoading: query.isLoading,
    createResponse: createResponseMutation.mutateAsync,
    updateResponse: updateResponseMutation.mutateAsync
  };
}

export function useSaveResponse() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: {
      inspection_id: string;
      template_item_id: string;
      response_value?: string;
      notes?: string;
      photo_urls?: string[];
    }) => {
      // Check if response exists
      const { data: existing } = await supabase
        .from('inspection_responses')
        .select('id')
        .eq('inspection_id', data.inspection_id)
        .eq('template_item_id', data.template_item_id)
        .maybeSingle();

      if (existing) {
        // Update existing
        const { error } = await supabase
          .from('inspection_responses')
          .update({
            response_value: data.response_value,
            notes: data.notes,
            photo_urls: data.photo_urls || []
          })
          .eq('id', existing.id);
        
        if (error) throw error;
      } else {
        // Insert new
        const { error } = await supabase
          .from('inspection_responses')
          .insert({
            inspection_id: data.inspection_id,
            template_item_id: data.template_item_id,
            response_value: data.response_value,
            notes: data.notes,
            photo_urls: data.photo_urls || []
          });
        
        if (error) throw error;
      }
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['inspection-responses', variables.inspection_id] });
    }
  });
}

// Photo upload
export function useUploadInspectionPhoto() {
  return useMutation({
    mutationFn: async (file: File) => {
      const fileExt = file.name.split('.').pop();
      const fileName = `${Date.now()}-${Math.random().toString(36).substring(7)}.${fileExt}`;
      const filePath = `inspection-photos/${fileName}`;

      const { error: uploadError } = await supabase.storage
        .from('inspection_photos')
        .upload(filePath, file);

      if (uploadError) throw uploadError;

      const signedUrl = await getSignedStorageUrl('inspection_photos', filePath);

      return signedUrl;
    },
    onError: (error: Error) => {
      toast({ title: 'Error uploading photo', description: error.message, variant: 'destructive' });
    }
  });
}
